import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAuth } from "common/context/AuthContext";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useAppInit from "common/hooks/useAppInit";
import useWhitelabelCustomization from "common/hooks/useWhitelabelCustomization";
import BaseLayout from "common/layouts/BaseLayout";
import WorkspaceLayout from "common/layouts/WorkspaceLayout";

import CrashPage from "./common/components/CrashPage";
import useCustomer from "./common/datahooks/useCustomer";
import Login from "./pages/login/Login";
import Onboarding from "./pages/onboarding/Onboarding";
import { baseRoutes, workspaceRoutes } from "./pages/routes";

function App() {
  const navigate = useNavigate();
  useAppInit();
  const { isAuthorized } = useAuth();
  const { customerInfo } = useCustomer();
  const { workspaces } = useWorkspaces();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite_token");
  const { whitelabelCustomization } = useWhitelabelCustomization();

  const isAppLoaded =
    (whitelabelCustomization && isAuthorized === false) ||
    (isAuthorized === true && customerInfo && workspaces);

  useEffect(() => {
    if (isAppLoaded) {
      document.getElementById("loader-container")?.remove();
    }
  }, [isAppLoaded]);

  useEffect(() => {
    if (inviteToken) {
      navigate(`/invitations/${inviteToken}`, { replace: true });
    }
  }, [inviteToken, navigate]);

  if (!isAppLoaded) {
    return null;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError, eventId }) => (
        <CrashPage resetError={resetError} eventId={eventId} />
      )}
    >
      <Routes>
        {whitelabelCustomization ? (
          <>
            {/*//TODO: REMOVE WORKSPACE ID FROM URL*/}
            {isAuthorized ? (
              <>
                <Route
                  path="workspaces/:workspaceId"
                  element={<WorkspaceLayout />}
                >
                  {workspaceRoutes.map(({ path, component }) => (
                    <Route key={path} path={path} element={component} />
                  ))}
                </Route>
                {/*//TODO: CLEAN THIS UP WHEN WORKSPACE ID IS REMOVED*/}
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={`workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`}
                      replace
                    />
                  }
                />
              </>
            ) : (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            )}
          </>
        ) : (
          <>
            <Route path="/onboarding" element={<Onboarding />} />

            {/* Base Routes */}
            <Route element={<BaseLayout />}>
              {baseRoutes.map(({ component, path }) => (
                <Route key={path} path={path} element={component} />
              ))}
            </Route>
            {/* Workspace routes */}
            <Route path="workspaces/:workspaceId" element={<WorkspaceLayout />}>
              {workspaceRoutes.map(({ path, component }) => (
                <Route key={path} path={path} element={component} />
              ))}
            </Route>
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    customerInfo.show_questionnaire
                      ? `onboarding?${searchParams.toString()}`
                      : `workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`
                  }
                  replace
                />
              }
            />
          </>
        )}
      </Routes>
    </Sentry.ErrorBoundary>
  );
}

export default App;
